import { useRef, useState, useLayoutEffect, type RefObject } from 'react'

/**
 * Auto-scales font size so content always fits its container.
 *
 * Measures text width in a detached offscreen <span> that mirrors the
 * target's font properties. Uses ResizeObserver to re-measure on container
 * resize. Ported from v56.2 lines 374-440.
 *
 * @returns [ref to attach to the container element, computed font size in px]
 */
export function useFitFont(text: string, baseSize: number): [RefObject<HTMLElement | null>, number] {
  const ref = useRef<HTMLElement | null>(null)
  const [size, setSize] = useState(baseSize)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return

    let cancelled = false

    const measure = () => {
      if (cancelled) return

      const computed = window.getComputedStyle(el)
      const probe = document.createElement('span')
      probe.style.cssText =
        'position:absolute;visibility:hidden;white-space:nowrap;' +
        'top:-9999px;left:0;pointer-events:none;margin:0;padding:0;'
      probe.style.fontFamily = computed.fontFamily
      probe.style.fontWeight = computed.fontWeight
      probe.style.fontVariantNumeric = computed.fontVariantNumeric
      probe.style.letterSpacing = computed.letterSpacing
      probe.style.fontSize = baseSize + 'px'
      probe.textContent = text || ''
      document.body.appendChild(probe)
      const contentWidth = probe.getBoundingClientRect().width
      document.body.removeChild(probe)

      if (cancelled) return
      const availWidth = el.clientWidth
      if (availWidth > 0 && contentWidth > availWidth) {
        // 0.96 = ~4% safety margin against sub-pixel rounding
        setSize(Math.max(12, Math.floor(baseSize * (availWidth / contentWidth) * 0.96)))
      } else {
        setSize(baseSize)
      }
    }

    measure()

    let ro: ResizeObserver | undefined
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure)
      ro.observe(el)
    }
    return () => {
      cancelled = true
      if (ro) ro.disconnect()
    }
  }, [text, baseSize])

  return [ref, size]
}
